import { createClient, type FunctionInvokeOptions } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Use implicit flow so password recovery links work even if opened on a
    // different device/browser than where the reset was requested. PKCE (the
    // default) stores a verifier in localStorage that won't be present
    // cross-device, causing silent failures. Implicit flow is acceptable here
    // because the admin app is an internal tool behind password auth.
    flowType: 'implicit',
  },
});

/**
 * Extract the real error message from a Supabase FunctionsHttpError.
 *
 * The Supabase JS client wraps non-2xx Edge Function responses as a generic
 * `FunctionsHttpError` with an unhelpful message. The raw `Response` object
 * (containing the actual JSON error body) is stored in `error.context`.
 * This helper reads that body so callers get the real error message.
 */
async function extractEdgeFunctionError(error: Error): Promise<Error> {
  const ctx = (error as { context?: Response }).context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = await ctx.json();
      const detail = body?.error ?? body?.message ?? error.message;
      return new Error(detail);
    } catch {
      // Response body couldn't be parsed — fall through
    }
  }
  return error;
}

/**
 * Check whether a JWT is expired or will expire within `marginSec` seconds.
 */
function isTokenExpired(token: string, marginSec = 30): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now() + marginSec * 1000;
  } catch {
    return true;
  }
}

/**
 * Merge an Authorization header into the invoke options.
 */
function withAuth(token: string, options?: FunctionInvokeOptions): FunctionInvokeOptions {
  return {
    ...options,
    headers: {
      ...(options?.headers instanceof Headers
        ? Object.fromEntries((options.headers as Headers).entries())
        : options?.headers),
      Authorization: `Bearer ${token}`,
    },
  };
}

/**
 * Invoke an Edge Function with a guaranteed-fresh session token.
 *
 * The access token is passed **explicitly** in the Authorization header so we
 * never rely on the Supabase client's internal cached token (which can be
 * stale due to timing gaps after getSession/refreshSession).
 *
 * If the token is expired or near-expiry, we force a refresh before calling.
 * On a 401 response, we refresh once and retry.
 */
export async function invokeFunction<T = unknown>(
  functionName: string,
  options?: FunctionInvokeOptions,
): Promise<{ data: T; error: null } | { data: null; error: Error }> {
  // 1. Get the current session
  let {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) {
    return { data: null, error: new Error('Not authenticated. Please sign in again.') };
  }

  // 2. If the token is expired or about to expire, force a refresh
  if (isTokenExpired(session.access_token)) {
    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !refreshData.session) {
      return { data: null, error: new Error('Session expired. Please sign in again.') };
    }
    session = refreshData.session;
  }

  // 3. Invoke with the token passed explicitly in headers
  const { data, error } = await supabase.functions.invoke<T>(
    functionName,
    withAuth(session.access_token, options),
  );

  if (error) {
    // 4. On 401, refresh once and retry with the new token
    const ctx = (error as { context?: Response }).context;
    if (ctx?.status === 401) {
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError || !refreshData.session) {
        return { data: null, error: new Error('Session expired. Please sign in again.') };
      }

      const retry = await supabase.functions.invoke<T>(
        functionName,
        withAuth(refreshData.session.access_token, options),
      );
      return retry.error
        ? { data: null, error: await extractEdgeFunctionError(retry.error) }
        : { data: retry.data as T, error: null };
    }

    return { data: null, error: await extractEdgeFunctionError(error) };
  }

  return { data: data as T, error: null };
}
